import { useCallback, useMemo, useRef, useState } from "react";

import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { convertDateToStringDDMMYYYY, downloadCsv, toCsvContent } from "../../../../../utils";
import { Toast } from "../../../../../widget";
import {
  KANBAN_COLUMN_LABELS,
  KANBAN_COLUMN_ORDER,
  OBJECT_TYPE_LABELS,
} from "../../../../../widget/general/TasksDashboard/constants";
import { TasksDashboardQueryFilters } from "../models";
import { buildFlatTasksRequest, buildTaskFilter, getVisibleStatuses, mapStatusToColumnId } from "../utils";

type SearchWorkflowTaskNode = SearchWorkflowTasksResponse["results"][number];

export interface UseTasksDashboardExportArgs {
  /** The one shared task-level filter object (BR-02828) — the export is driven by this and nothing else. */
  filters: TasksDashboardQueryFilters;
}

export interface UseTasksDashboardExportReturnData {
  /** Re-queries the whole filtered set and downloads it as CSV. */
  onExportTable: () => void;
  /** True while the multi-page fetch is running — the List disables the action. */
  isExporting: boolean;
}

/** Rows per page while paging the export. Larger than the on-screen page size: nothing renders. */
const EXPORT_PAGE_SIZE = 200;

/**
 * Ceiling on an export, so a mispaging server (or an organisation far larger than this feature was
 * scoped for) can't page forever. Hitting it is reported, never silently truncated.
 */
const EXPORT_MAX_ROWS = 10000;

const EXPORT_HEADERS = ["Object type", "Object", "Task", "Tags", "Status", "Deadline", "Assignee"];

/** The Tags cell, in the same order the table renders the pills. */
const toTagsCell = (node: SearchWorkflowTaskNode): string =>
  [
    node.isMandatory ? "Mandatory" : undefined,
    node.isMilestone ? "Milestone" : undefined,
    node.step ? "Workflow" : "General",
    ...node.tags.map((tag) => tag.tagName),
  ]
    .filter(Boolean)
    .join("; ");

const toObjectTypeCell = (objectType: string | null): string =>
  objectType ? (OBJECT_TYPE_LABELS[objectType.toLowerCase()] ?? objectType) : "";

const toExportRow = (node: SearchWorkflowTaskNode): string[] => [
  toObjectTypeCell(node.parentObjectType),
  node.parentObjectName ?? "",
  node.name,
  toTagsCell(node),
  KANBAN_COLUMN_LABELS[mapStatusToColumnId(node.status)],
  node.deadlineDate ? convertDateToStringDDMMYYYY(node.deadlineDate) : "",
  node.assignee?.name ?? "",
];

/**
 * Order the flat export the way the table reads: groups by object type then object name (the group
 * query's server-side order, BR-02826), each group's rows by status section in lane order, and then
 * by deadline — so the file reconciles line-for-line with what expanding every section would show.
 */
const compareExportRows = (a: SearchWorkflowTaskNode, b: SearchWorkflowTaskNode): number => {
  const byType = (a.parentObjectType ?? "").localeCompare(b.parentObjectType ?? "");
  if (byType !== 0) return byType;

  const byName = (a.parentObjectName ?? "").localeCompare(b.parentObjectName ?? "");
  if (byName !== 0) return byName;

  const byStatus =
    KANBAN_COLUMN_ORDER.indexOf(mapStatusToColumnId(a.status)) -
    KANBAN_COLUMN_ORDER.indexOf(mapStatusToColumnId(b.status));
  if (byStatus !== 0) return byStatus;

  // Undated tasks last, so the section's dated rows keep the ascending order the screen shows.
  const deadlineA = a.deadlineDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const deadlineB = b.deadlineDate?.getTime() ?? Number.MAX_SAFE_INTEGER;

  return deadlineA - deadlineB;
};

/**
 * "Table options → Export table" (KAN-9564 §6, decision B — full re-query).
 *
 * Under lazy grouping the client only holds the rows for the sections the user has expanded, so
 * serialising what is in memory would export nothing at all from a freshly opened List. Instead the
 * export re-pages the leaf connection with **the same shared filter object** the group headers and
 * the rows are built from — with no `parentObjectUuid` or `status` narrowing — so the file is the
 * whole filtered table and its total reconciles with the group `taskCount`s (BR-02828).
 */
export const useTasksDashboardExport = ({
  filters,
}: UseTasksDashboardExportArgs): UseTasksDashboardExportReturnData => {
  const [isExporting, setIsExporting] = useState(false);

  // The same shared filter the headers and the leaf rows are built from.
  const sharedFilter = useMemo(() => buildTaskFilter(filters), [filters]);

  /*
    The in-flight guard is a ref, not the state above: two clicks in the same tick would both read
    `isExporting === false` and start a second multi-page fetch.

    Deliberately no "is still mounted" ref guarding the setState below: under StrictMode the effect
    that would clear such a ref runs mount → cleanup → mount, so it latches false on the first
    remount and the control sticks on "Exporting…" forever. Setting state after unmount is a no-op in
    React 18, so the guard bought nothing.
  */
  const isExportingRef = useRef(false);

  const onExportTable = useCallback((): void => {
    if (isExportingRef.current) return;

    isExportingRef.current = true;
    setIsExporting(true);

    // The hide toggles narrow the export exactly as they narrow the headers, so a hidden status
    // never appears in a file whose counts are supposed to match the screen.
    const visibleStatuses = getVisibleStatuses(filters);

    const pageAll = async (): Promise<SearchWorkflowTaskNode[] | undefined> => {
      const nodes: SearchWorkflowTaskNode[] = [];
      let afterCursor: string | null = null;

      for (;;) {
        // Sequential by necessity: each page's cursor comes from the one before it.
        // eslint-disable-next-line no-await-in-loop
        const response = await searchWorkflowTasks(
          buildFlatTasksRequest(sharedFilter, EXPORT_PAGE_SIZE, afterCursor),
          visibleStatuses && { operator: "in", value: visibleStatuses }
        );

        if (response.status !== Status.Success || !response.data) return undefined;

        nodes.push(...response.data.results);

        if (!response.data.paging.hasNextPage || nodes.length >= EXPORT_MAX_ROWS) {
          if (response.data.paging.hasNextPage) {
            Toast.warning({
              message: `Export limited to the first ${EXPORT_MAX_ROWS} tasks. Narrow the filters to export the rest.`,
            });
          }
          return nodes;
        }

        afterCursor = response.data.paging.endCursor || null;
        // A page that reports a further page but hands back no cursor would loop forever.
        if (!afterCursor) return nodes;
      }
    };

    pageAll()
      .then((nodes) => {
        if (!nodes) {
          Toast.error({ message: "Couldn't export the table - please try again later." });
          return;
        }

        const rows = [...nodes].sort(compareExportRows).map(toExportRow);
        downloadCsv(`tasks_${new Date().toISOString()}.csv`, toCsvContent(EXPORT_HEADERS, rows));
      })
      .catch(() => {
        Toast.error({ message: "Couldn't export the table - please try again later." });
      })
      .finally(() => {
        isExportingRef.current = false;
        setIsExporting(false);
      });
  }, [sharedFilter, filters]);

  return { onExportTable, isExporting };
};
